import { useCallback, useEffect, useRef, useState } from 'react';
import type { Stroke } from '@frisk/shared';
import { api } from '../api';
import { kicker, screenTitle, t } from '../theme';
import { useStore } from '../store';

/**
 * Freehand scribble board for the fridge panel.
 *
 * Strokes are vectors, not a rasterised canvas: a fraction of the storage,
 * crisp at any pixel ratio, and it is what makes undo and stroke-erase
 * possible at all.
 *
 * Both axes are normalised by the board's *width* so the aspect ratio is
 * preserved — normalising y by height would squash the drawing whenever the
 * viewport changed shape.
 */
const COLOURS = [
  'oklch(0.97 0.008 80)', // chalk
  'oklch(0.82 0.16 130)', // frisk green
  'oklch(0.83 0.15 80)', // amber
  'oklch(0.72 0.17 25)', // red
  'oklch(0.75 0.14 250)', // blue
  'oklch(0.72 0.16 320)', // violet
];

const WIDTHS = [0.0025, 0.005, 0.011];
const ERASER_RADIUS = 0.022;
const SAVE_DEBOUNCE_MS = 700;

/**
 * Board coordinates are 0..1, so four decimals is ~0.12px on a 1194px panel —
 * far finer than any nib. Full float precision serialises to ~60 bytes per
 * point, which is most of the payload for nothing.
 */
const round4 = (n: number) => Math.round(n * 10_000) / 10_000;
const round2 = (n: number) => Math.round(n * 100) / 100;

function compact(strokes: Stroke[]): Stroke[] {
  return strokes.map((stroke) => ({
    ...stroke,
    points: stroke.points.map((pt) =>
      pt.p === undefined
        ? { x: round4(pt.x), y: round4(pt.y) }
        : { x: round4(pt.x), y: round4(pt.y), p: round2(pt.p) },
    ),
  }));
}

type Tool = 'pen' | 'eraser';

export function Notes() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const drawingRef = useRef<Stroke | null>(null);
  /** Pointer that owns the current stroke; everything else is ignored. */
  const activePointerRef = useRef<number | null>(null);
  const penSeenRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastSavedRef = useRef<string>('');

  const notesVersion = useStore((s) => s.notesVersion);
  const flash = useStore((s) => s.flash);

  const [tool, setTool] = useState<Tool>('pen');
  const [colour, setColour] = useState(COLOURS[0]!);
  const [width, setWidth] = useState(WIDTHS[1]!);
  const [strokeCount, setStrokeCount] = useState(0);
  const [saving, setSaving] = useState(false);

  // ---------------------------------------------------------------- render

  const scale = () => canvasRef.current?.getBoundingClientRect().width ?? 1;

  const drawSegment = useCallback(
    (ctx: CanvasRenderingContext2D, stroke: Stroke, from: number, to: number, unit: number) => {
      ctx.strokeStyle = stroke.color;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let i = Math.max(1, from); i <= to; i++) {
        const a = stroke.points[i - 1]!;
        const b = stroke.points[i]!;
        // Pressure modulates width per segment; a single lineWidth per path
        // would throw away everything the Pencil reports.
        const pressure = ((a.p ?? 1) + (b.p ?? 1)) / 2;
        ctx.lineWidth = Math.max(1, stroke.width * unit * (0.4 + 0.6 * pressure));
        ctx.beginPath();
        ctx.moveTo(a.x * unit, a.y * unit);
        ctx.lineTo(b.x * unit, b.y * unit);
        ctx.stroke();
      }
      if (stroke.points.length === 1 && from <= 0) {
        // A single tap should still leave a dot.
        const only = stroke.points[0]!;
        ctx.fillStyle = stroke.color;
        ctx.beginPath();
        ctx.arc(only.x * unit, only.y * unit, Math.max(1, (stroke.width * unit) / 2), 0, Math.PI * 2);
        ctx.fill();
      }
    },
    [],
  );

  const redrawAll = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(rect.width * dpr) || canvas.height !== Math.round(rect.height * dpr)) {
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    for (const stroke of strokesRef.current) drawSegment(ctx, stroke, 1, stroke.points.length - 1, rect.width);
  }, [drawSegment]);

  // ------------------------------------------------------------ load/save

  const load = useCallback(async () => {
    try {
      const board = await api.getNotes();
      if (board.updatedAt === lastSavedRef.current) return; // our own write
      strokesRef.current = board.strokes;
      lastSavedRef.current = board.updatedAt;
      setStrokeCount(board.strokes.length);
      redrawAll();
    } catch {
      flash('Notizen konnten nicht geladen werden');
    }
  }, [redrawAll, flash]);

  useEffect(() => {
    void load();
  }, [load]);

  // Another panel edited the board. Never clobber a stroke in progress.
  useEffect(() => {
    if (notesVersion === 0 || drawingRef.current) return;
    void load();
  }, [notesVersion, load]);

  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const saved = await api.putNotes(compact(strokesRef.current));
        lastSavedRef.current = saved.updatedAt;
      } catch {
        flash('Notizen konnten nicht gespeichert werden');
      } finally {
        setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
  }, [flash]);

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  useEffect(() => {
    const onResize = () => redrawAll();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [redrawAll]);

  // -------------------------------------------------------------- input

  const toBoard = (event: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const unit = rect.width;
    return { x: (event.clientX - rect.left) / unit, y: (event.clientY - rect.top) / unit };
  };

  /** Pencil 2 reports its flipped eraser end as button 5 / buttons bit 32. */
  const isEraserEnd = (e: React.PointerEvent) => e.pointerType === 'pen' && (e.buttons & 32) !== 0;

  const eraseAt = (x: number, y: number) => {
    const before = strokesRef.current.length;
    strokesRef.current = strokesRef.current.filter(
      (stroke) =>
        !stroke.points.some((pt) => (pt.x - x) ** 2 + (pt.y - y) ** 2 <= ERASER_RADIUS ** 2),
    );
    return strokesRef.current.length !== before;
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== null) return;
    if (e.pointerType === 'pen') penSeenRef.current = true;
    // Palm rejection: once a Pencil has been used, ignore touches entirely.
    if (e.pointerType === 'touch' && penSeenRef.current) return;

    try {
      // Keeps the stroke attached to this pointer even if it leaves the canvas.
      // Throws for a pointer the browser no longer tracks, which must not take
      // the whole handler down with it.
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* drawing still works without capture */
    }
    activePointerRef.current = e.pointerId;

    const { x, y } = toBoard(e);

    if (tool === 'eraser' || isEraserEnd(e)) {
      if (eraseAt(x, y)) {
        redrawAll();
        setStrokeCount(strokesRef.current.length);
      }
      return;
    }

    const stroke: Stroke = {
      color: colour,
      width,
      points: [{ x, y, p: e.pointerType === 'pen' ? e.pressure || 0.5 : 1 }],
    };
    drawingRef.current = stroke;
    strokesRef.current = [...strokesRef.current, stroke];

    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) drawSegment(ctx, stroke, 0, 0, scale());
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== e.pointerId) return;

    // Coalesced events are the difference between a smooth Pencil line and a
    // chunky one: Safari batches up to ~120 samples per frame.
    const samples = typeof e.nativeEvent.getCoalescedEvents === 'function'
      ? e.nativeEvent.getCoalescedEvents()
      : [e.nativeEvent];

    if (tool === 'eraser' || isEraserEnd(e)) {
      let changed = false;
      for (const sample of samples) {
        const { x, y } = toBoard(sample);
        if (eraseAt(x, y)) changed = true;
      }
      if (changed) {
        redrawAll();
        setStrokeCount(strokesRef.current.length);
      }
      return;
    }

    const stroke = drawingRef.current;
    if (!stroke) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const unit = scale();
    for (const sample of samples) {
      const { x, y } = toBoard(sample);
      stroke.points.push({ x, y, p: e.pointerType === 'pen' ? sample.pressure || 0.5 : 1 });
      drawSegment(ctx, stroke, stroke.points.length - 1, stroke.points.length - 1, unit);
    }
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== e.pointerId) return;
    activePointerRef.current = null;
    if (drawingRef.current) {
      drawingRef.current = null;
      setStrokeCount(strokesRef.current.length);
    }
    scheduleSave();
  };

  // -------------------------------------------------------------- actions

  const undo = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current = strokesRef.current.slice(0, -1);
    setStrokeCount(strokesRef.current.length);
    redrawAll();
    scheduleSave();
  };

  const clearAll = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current = [];
    setStrokeCount(0);
    redrawAll();
    scheduleSave();
    flash('Notizen gelöscht');
  };

  const toolButton = (label: string, active: boolean, onTap: () => void) => (
    <div
      className="press"
      onClick={onTap}
      style={{
        height: 56,
        padding: '0 20px',
        borderRadius: 14,
        background: active ? 'oklch(0.32 0.012 60)' : t.surface,
        border: active ? `1px solid ${t.accent}` : t.hairline,
        display: 'flex',
        alignItems: 'center',
        fontSize: 19,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '34px 38px 30px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20 }}>
        <div style={{ flex: 'none' }}>
          <div style={kicker}>{saving ? 'WIRD GESPEICHERT …' : `${strokeCount} STRICHE`}</div>
          <div style={{ ...screenTitle, whiteSpace: 'nowrap' }}>Notizen</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* colours */}
          <div style={{ display: 'flex', gap: 8, padding: 6, background: t.surface, borderRadius: 16, border: t.hairline }}>
            {COLOURS.map((c) => (
              <div
                key={c}
                className="press"
                onClick={() => {
                  setColour(c);
                  setTool('pen');
                }}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  background: c,
                  border: colour === c && tool === 'pen' ? '3px solid oklch(1 0 0 / 0.9)' : '3px solid transparent',
                  boxShadow: 'inset 0 0 0 1px oklch(0 0 0 / 0.25)',
                }}
              />
            ))}
          </div>

          {/* nib sizes */}
          <div style={{ display: 'flex', gap: 8, padding: 6, background: t.surface, borderRadius: 16, border: t.hairline }}>
            {WIDTHS.map((w) => (
              <div
                key={w}
                className="press"
                onClick={() => {
                  setWidth(w);
                  setTool('pen');
                }}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: width === w && tool === 'pen' ? 'oklch(0.32 0.012 60)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div style={{ width: w * 1400, height: w * 1400, borderRadius: 999, background: t.ink }} />
              </div>
            ))}
          </div>

          {toolButton('Radierer', tool === 'eraser', () => setTool(tool === 'eraser' ? 'pen' : 'eraser'))}
          {toolButton('Zurück', false, undo)}
          {toolButton('Alles löschen', false, clearAll)}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          marginTop: 20,
          borderRadius: 26,
          background: 'oklch(0.185 0.012 60)',
          border: t.hairline,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            // Without this the browser claims the gesture for panning and the
            // Pencil never draws.
            touchAction: 'none',
            cursor: tool === 'eraser' ? 'cell' : 'crosshair',
          }}
        />
        {strokeCount === 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              fontSize: 24,
              color: 'oklch(0.45 0.012 60)',
            }}
          >
            Mit dem Pencil oder dem Finger schreiben
          </div>
        )}
      </div>
    </div>
  );
}
